import {Component, Fragment} from 'react'
import {Field, reduxForm} from 'redux-form'
import Plus from 'react-feather/dist/icons/plus.js'
import Shield from 'react-feather/dist/icons/shield.js'
import Radio from 'react-feather/dist/icons/radio.js'
import Key from 'react-feather/dist/icons/key.js'

import validate from './validate.js'
import Input from '../../Form/Field/Input.jsx'
import Select from '../../Form/Field/Select.jsx'
import Date from '../../Form/Field/Date.jsx'
import UploadDropZone from '../../Form/Field/UploadDropZone.jsx'
import FormGlobalErrors from '../../Form/FormGlobalErrors.jsx'
import createOnSubmitFailHandler from '../../Form/createOnSubmitFailHandler.js'
import Loader from '../../../../shared/components/Loader/Loader.jsx'
import CardRow from '../../../../shared/components/Styled/Card/CardRow.jsx'
import CardCol from '../../../../shared/components/Styled/Card/CardCol.jsx'
import conf from '../../../conf.js'
import {
  clearanceTypes,
  clearanceStatuses,
  defenseLevels,
  defenseScopes,
  radiationProtectionCategories,
} from '../../../../shared/services/clearanceConstants.js'

import './ClearanceForm.scss'

export const name = 'ClearanceForm'

const typeButtons = [
  {...clearanceTypes.defense, icon: Shield},
  {...clearanceTypes.radiation_protection, icon: Radio},
  {...clearanceTypes.site_access, icon: Key},
]

class ClearanceForm extends Component {
  componentDidUpdate(prevProps) {
    const editingChanged = (prevProps.editing?.id ?? null) !== (this.props.editing?.id ?? null)
    const typeChangedWhileAdding = !this.props.editing && prevProps.selectedType !== this.props.selectedType
    if (editingChanged || typeChangedWhileAdding) {
      this.props.initialize(this.props.initialValues)
    }
  }

  handleSubmit = (values) => {
    const subResult = this.props.handleSubmit(values)
    if (!subResult.then) {
      return subResult
    }
    return subResult.then((data) => {
      this.props.reset()
      return data
    })
  }

  render() {
    const {
      selectedType, editing, onTypeChange, onCancel, file, onFileChange,
      existingDocument, existingDocumentUrl,
    } = this.props

    return (
      <div id={name} className="clearance-form mt-5">
        <form autoComplete="off" onSubmit={this.handleSubmit}>
          <FormGlobalErrors className="mt-0" errors={this.props.error} />

          <div className="clearance-form-type-selector">
            {typeButtons.map((type) => (
              <button
                key={type.value}
                type="button"
                disabled={!!editing}
                onClick={() => onTypeChange(type.value)}
                className={`btn btn-${selectedType !== type.value ? 'outline-' : ''}secondary`}
              >
                <type.icon size={30} className="mb-1" /> {type.label}
              </button>
            ))}
          </div>

          <CardRow gap="20px" wrap="wrap">
            <CardCol flex={1} className="formations-form-field-col">
              <Field label="Statut" name="status" component={Select}>
                {Object.values(clearanceStatuses).map((status) => (
                  <option value={status.value} key={status.value}>{status.label}</option>
                ))}
              </Field>
            </CardCol>

            {selectedType === clearanceTypes.defense.value && (
              <Fragment>
                <CardCol flex={1} className="formations-form-field-col">
                  <Field label="Niveau" name="level" component={Select}>
                    {Object.values(defenseLevels).map((level) => (
                      <option value={level.value} key={level.value}>{level.label}</option>
                    ))}
                  </Field>
                </CardCol>
                <CardCol flex={1} className="formations-form-field-col">
                  <Field label="Périmètre" name="scope" component={Select}>
                    {Object.values(defenseScopes).map((scope) => (
                      <option value={scope.value} key={scope.value}>{scope.label}</option>
                    ))}
                  </Field>
                </CardCol>
              </Fragment>
            )}

            {selectedType === clearanceTypes.radiation_protection.value && (
              <CardCol flex={1} className="formations-form-field-col">
                <Field label="Catégorie" name="category" component={Select}>
                  <option value=""></option>
                  {Object.values(radiationProtectionCategories).map((category) => (
                    <option value={category.value} key={category.value}>{category.label}</option>
                  ))}
                </Field>
              </CardCol>
            )}

            {selectedType === clearanceTypes.site_access.value && (
              <CardCol flex={1} className="formations-form-field-col">
                <Field label="Site / exploitant" name="operatingSite" type="text" maxLength={255} component={Input}/>
              </CardCol>
            )}
          </CardRow>

          <CardRow gap="20px" wrap="wrap">
            <CardCol flex={1} className="formations-form-field-col">
              <Field label="Date d'obtention" name="obtainedAt" dateFormat="DD/MM/YYYY" component={Date}/>
            </CardCol>
            <CardCol flex={1} className="formations-form-field-col">
              <Field label="Date d'expiration" name="expiresAt" dateFormat="DD/MM/YYYY" component={Date}/>
            </CardCol>
          </CardRow>

          <CardRow gap="20px" wrap="wrap">
            <CardCol flex={1} className="formations-form-field-col">
              <Field label="Autorité émettrice" name="issuingAuthority" type="text" maxLength={255} component={Input}/>
            </CardCol>
            <CardCol flex={1} className="formations-form-field-col">
              <Field label="Restriction de nationalité" name="nationalityRestriction" type="text" maxLength={255} component={Input}/>
            </CardCol>
          </CardRow>

          <CardRow>
            <CardCol flex={1}>
              {!file && existingDocument && (
                <div className="clearance-form-existing-document callout callout-secondary mb-2">
                  Justificatif actuel : <b>{existingDocument.name}</b>
                  {existingDocumentUrl && (
                    <a href={existingDocumentUrl} target="_blank" rel="noopener noreferrer" className="ml-2">
                      Voir le justificatif
                    </a>
                  )}
                  <div className="text-muted">Déposez un nouveau fichier ci-dessous pour le remplacer.</div>
                </div>
              )}
              <UploadDropZone
                input={{name: 'uploadedFile', value: file, onChange: onFileChange}}
                dropText="Déposer le justificatif en Drag'n'Drop"
                uploadText={existingDocument ? 'Remplacer le justificatif' : 'Sélectionner le justificatif'}
                uploadModifyText="Modifier le justificatif"
                uploadDeleteText="Retirer le justificatif"
                deletable={true}
                deleteFile={() => onFileChange(null)}
                onSubmit={(formData) => Promise.resolve(formData.get('uploadedFile'))}
                maxSize={conf.server.uploadMaxSize}
                acceptText=".doc, .docx, .pdf"
                accept={conf.allowedMimeTypes.clearanceDocument}
              >
                {file && <div>Fichier sélectionné : {file.name}</div>}
              </UploadDropZone>
            </CardCol>
          </CardRow>

          <CardRow className="mt-4" justify="flex-end" gap="10px">
            {editing && (
              <CardCol>
                <button type="button" onClick={onCancel} className="btn btn-outline-primary">Annuler</button>
              </CardCol>
            )}
            <CardCol>
              <button type="submit" className="mb-3 btn btn-secondary">
                {!editing ? <Plus/> : null}
                {editing ? 'Enregistrer les modifications' : 'Ajouter l\'habilitation'}
              </button>
            </CardCol>
          </CardRow>
        </form>
        <Loader visible={this.props.submitting}></Loader>
      </div>
    )
  }
}

export default reduxForm({
  form: name,
  onSubmitFail: createOnSubmitFailHandler(name),
  validate,
})(ClearanceForm)
